import { useRef, useState, type DragEvent } from 'react';
import { useI18n } from '../../i18n';
import { formatBytes } from '../../lib/format';

/**
 * Drag-and-drop for a WoS / Scopus / RIS / PubMed export.
 *
 * The format is sniffed server-side by `journalmap.io.base.read_file`, so the
 * extension check here is only there to catch an obvious mistake early — a PDF
 * or a spreadsheet — not to decide the reader.
 */

/** Extensions the four supported readers actually come in. */
const ACCEPTED = ['.txt', '.csv', '.ris', '.nbib', '.bib', '.tsv'] as const;

export function FileDropzone({
  file,
  onFile,
  maxBytes,
  disabled,
}: {
  file: File | null;
  onFile: (file: File | null) => void;
  maxBytes: number;
  disabled?: boolean;
}) {
  const { t, tag } = useI18n();
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const accept = (candidate: File): void => {
    const dot = candidate.name.lastIndexOf('.');
    const ext = dot >= 0 ? candidate.name.slice(dot).toLowerCase() : '';
    if (!ACCEPTED.includes(ext as (typeof ACCEPTED)[number])) {
      setError(t('upload.badType', { ext: ext || candidate.name }));
      return;
    }
    if (candidate.size > maxBytes) {
      setError(
        t('upload.tooBig', {
          size: formatBytes(candidate.size, tag),
          limit: formatBytes(maxBytes, tag),
        }),
      );
      return;
    }
    setError(null);
    onFile(candidate);
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    if (disabled) return;
    const dropped = event.dataTransfer.files.item(0);
    if (dropped) accept(dropped);
  };

  if (file) {
    return (
      <div className="stack stack--tight">
        <div className="file-card">
          <span className="file-card__name">{file.name}</span>
          <span className="file-card__meta">{formatBytes(file.size, tag)}</span>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            className="button button--quiet"
            disabled={disabled}
            onClick={() => {
              onFile(null);
              setError(null);
              if (inputRef.current) inputRef.current.value = '';
            }}
          >
            {t('upload.remove')}
          </button>
        </div>
        <p className="field__hint">{t('upload.formats', { size: formatBytes(maxBytes, tag) })}</p>
      </div>
    );
  }

  return (
    <div className="stack stack--tight">
      <div
        className={dragging ? 'dropzone dropzone--active' : 'dropzone'}
        role="button"
        tabIndex={0}
        aria-disabled={disabled}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            if (!disabled) inputRef.current?.click();
          }
        }}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
      >
        <span>{dragging ? t('upload.dropActive') : t('upload.drop')}</span>
        <span className="faint">{t('upload.formats', { size: formatBytes(maxBytes, tag) })}</span>
      </div>

      <input
        ref={inputRef}
        className="visually-hidden"
        type="file"
        accept={ACCEPTED.join(',')}
        disabled={disabled}
        onChange={(event) => {
          const chosen = event.target.files?.item(0);
          if (chosen) accept(chosen);
        }}
      />

      {error ? <p className="field__error">{error}</p> : null}
    </div>
  );
}
